import { api } from "@/lib/api";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import {
  buildHomeHrefAfterWorkspacePick,
  isServiceHiredInWorkspace,
  orderWorkspaceId,
  type OrderWorkspaceRef,
} from "@/lib/billinGoRoutes";
import {
  persistLastWorkspace,
  readLastWorkspace,
  readSessionWorkspacePick,
} from "@/lib/lastWorkspaceStorage";
import {
  getWorkspaceIdFromLatestOrderForService,
  resolveWorkspaceForService,
} from "@/lib/resolveWorkspaceForService";

function workspaceExists(
  workspaces: Array<{ id: string }>,
  workspaceId: string | null | undefined,
): boolean {
  const id = workspaceId?.trim();
  if (!id) return false;
  return workspaces.some((w) => w.id === id);
}

/** Buyer orders (all workspaces) for hired checks after workspace pick. */
export async function listBuyerOrders(): Promise<OrderWorkspaceRef[]> {
  const response = await api.orders.list({ role: "buyer" });
  if (Array.isArray(response)) return response as OrderWorkspaceRef[];
  if (response && typeof response === "object") {
    const r = response as Record<string, unknown>;
    if (Array.isArray(r.orders)) return r.orders as OrderWorkspaceRef[];
    if (Array.isArray(r.data)) return r.data as OrderWorkspaceRef[];
  }
  return [];
}

/** Same check as the service page: workspace+service order exists (then buyer list fallback). */
export async function checkServiceHiredInWorkspace(
  workspaceId: string,
  serviceId: string,
): Promise<boolean | null> {
  const wid = workspaceId.trim();
  const sid = serviceId.trim();
  if (!wid || !sid) return false;

  try {
    const order = await api.orders.getByWorkspaceAndService(wid, sid);
    if (order) return true;
  } catch {
    // Hairpin / proxy 500 — fall back to the buyer list.
  }

  try {
    const orders = await listBuyerOrders();
    return isServiceHiredInWorkspace(orders, sid, wid);
  } catch {
    // Unknown (ECONNREFUSED etc.) — do not treat as unhired.
    return null;
  }
}

/**
 * Local orders are usually scoped to the *current* shop.
 * Missing the target shop ≠ unhired — that was flipping hired shops to
 * `source=marketplace` when switching workspaces.
 */
function localHireStatus(
  orders: OrderWorkspaceRef[] | null | undefined,
  serviceId: string,
  workspaceId: string,
): "hired" | "unhired" | "unknown" {
  const list = Array.isArray(orders) ? orders : [];
  if (isServiceHiredInWorkspace(list, serviceId, workspaceId)) return "hired";
  const sawTarget = list.some(
    (order) => orderWorkspaceId(order) === workspaceId.trim(),
  );
  return sawTarget ? "unhired" : "unknown";
}

/** Switch shops immediately from local orders; confirm hire status in the background. */
export function navigateHomeAfterWorkspacePick(
  navigate: (href: string) => void,
  workspaceId: string,
  serviceId: string | null | undefined,
  orders: OrderWorkspaceRef[] | null | undefined,
  opts?: { setting?: boolean },
): void {
  const sid = (serviceId || BILLIN_SERVICE_ID).trim();
  const local = localHireStatus(orders, sid, workspaceId);
  // Unknown (empty / other-shop cache): keep hired chrome so a hired shop
  // does not flash the marketplace hire chat, then correct from the API.
  const isHired = local !== "unhired";
  navigate(
    buildHomeHrefAfterWorkspacePick(workspaceId, sid, {
      isHired,
      setting: opts?.setting,
    }),
  );
  void checkServiceHiredInWorkspace(workspaceId, sid)
    .then((confirmed) => {
      if (confirmed == null || confirmed === isHired) return;
      navigate(
        buildHomeHrefAfterWorkspacePick(workspaceId, sid, {
          isHired: confirmed,
          setting: opts?.setting,
        }),
      );
    })
    .catch(() => {});
}

/**
 * Workspace to highlight in the pick-shop modal:
 * URL → localStorage → Billin API resolve → first workspace.
 */
export async function resolveWorkspacePickHint(
  workspaces: Array<{ id: string }>,
  opts?: { urlWorkspaceId?: string | null },
): Promise<string | null> {
  if (!workspaces.length) return null;

  const fromUrl = opts?.urlWorkspaceId?.trim();
  if (workspaceExists(workspaces, fromUrl)) return fromUrl!;

  const stored = readLastWorkspace();
  if (workspaceExists(workspaces, stored)) return stored!;

  const fromApi = await resolveWorkspaceForService(BILLIN_SERVICE_ID);
  if (workspaceExists(workspaces, fromApi)) return fromApi!;

  return workspaces[0]?.id ?? null;
}

function pickOwnedWorkspaceId(
  workspaces: Array<{ id: string }>,
  workspaceId: string | null | undefined,
): string | null {
  const id = workspaceId?.trim();
  if (!id) return null;
  return workspaceExists(workspaces, id) ? id : null;
}

function mostRecentWorkspaceId(
  workspaces: Array<{ id: string; updatedAt?: string; createdAt?: string }>,
): string | null {
  if (!workspaces.length) return null;
  const sorted = [...workspaces].sort((a, b) => {
    const ta = Date.parse(a.updatedAt || a.createdAt || "") || 0;
    const tb = Date.parse(b.updatedAt || b.createdAt || "") || 0;
    return tb - ta;
  });
  return sorted[0]?.id ?? null;
}

/**
 * First logged-in home visit (no service selected).
 * If the URL already has a workspace, keep that shop.
 * Otherwise: hired Bill-Lin → last work workspace; never hired → last shop.
 */
export async function resolveHomeLandingHref(opts: {
  workspaces: Array<{ id: string; updatedAt?: string; createdAt?: string }>;
  urlWorkspaceId?: string | null;
}): Promise<string | null> {
  const { workspaces } = opts;
  if (!workspaces.length) return null;

  const urlWid = pickOwnedWorkspaceId(workspaces, opts.urlWorkspaceId);
  if (urlWid) {
    persistLastWorkspace(urlWid);
    const isHired = await checkServiceHiredInWorkspace(
      urlWid,
      BILLIN_SERVICE_ID,
    );
    if (isHired == null) return null;
    return buildHomeHrefAfterWorkspacePick(urlWid, BILLIN_SERVICE_ID, {
      isHired,
    });
  }

  const sessionWid = pickOwnedWorkspaceId(
    workspaces,
    readSessionWorkspacePick(),
  );
  if (sessionWid) {
    persistLastWorkspace(sessionWid);
    const isHired = await checkServiceHiredInWorkspace(
      sessionWid,
      BILLIN_SERVICE_ID,
    );
    if (isHired == null) return null;
    return buildHomeHrefAfterWorkspacePick(sessionWid, BILLIN_SERVICE_ID, {
      isHired,
    });
  }

  const lastWid = pickOwnedWorkspaceId(workspaces, readLastWorkspace());
  const lastHired = lastWid
    ? await checkServiceHiredInWorkspace(lastWid, BILLIN_SERVICE_ID)
    : false;
  if (lastWid && lastHired) {
    persistLastWorkspace(lastWid);
    return buildHomeHrefAfterWorkspacePick(lastWid, BILLIN_SERVICE_ID, {
      isHired: true,
    });
  }
  if (lastWid && lastHired == null) return null;

  let latestOrderWorkspaceId: string | null = null;
  try {
    latestOrderWorkspaceId =
      await getWorkspaceIdFromLatestOrderForService(BILLIN_SERVICE_ID);
  } catch {
    latestOrderWorkspaceId = null;
  }
  const latestHired = pickOwnedWorkspaceId(
    workspaces,
    latestOrderWorkspaceId,
  );
  if (latestHired) {
    persistLastWorkspace(latestHired);
    return buildHomeHrefAfterWorkspacePick(latestHired, BILLIN_SERVICE_ID, {
      isHired: true,
    });
  }

  const hireWid = lastWid || mostRecentWorkspaceId(workspaces);
  if (!hireWid) return null;
  persistLastWorkspace(hireWid);
  return buildHomeHrefAfterWorkspacePick(hireWid, BILLIN_SERVICE_ID, {
    isHired: false,
  });
}
